import type { IFormField } from '../../../../../models/Api';
import { isSurveySection, type SectionedSurveyForm, type SurveySection } from '../../types';

/** A resolved render group: either a headed section or ungrouped flat questions. */
export interface SectionGroup {
	/** undefined ⇒ ungrouped flat questions (no section header rendered). */
	section?: SurveySection;
	fields: IFormField<string>[];
}

/**
 * Group visible fields by their owning section in document order.
 *
 * Returns `null` when the form has NO `SurveySection` parts — the caller
 * must fall back to V1 flat rendering. Sections with zero visible questions
 * are omitted (handles both empty-section suppression and section-level
 * hiding, since `computeVisibleFields` already excludes questions from
 * hidden sections).
 *
 * @param surveyForm - The V2 survey form whose `surveyParts` may include
 *   `SurveySection` containers. Pass `undefined` for V1 surveys.
 * @param fields - The flat `customForm.fields` array that carries render
 *   data (label, options, fieldType, isHidden). Questions join to these by
 *   `question.fieldId === field.id`.
 * @param visibleFieldIds - The set of field IDs that passed conditional-logic
 *   evaluation. Pass `undefined` to mean "show all" (V1 / no logic defined).
 */
export function buildSectionGroups(
	surveyForm: SectionedSurveyForm | undefined,
	fields: IFormField<string>[],
	visibleFieldIds: Set<string> | undefined
): SectionGroup[] | null {
	const parts = surveyForm?.surveyParts ?? [];

	// V1 fallback: no section parts → signal the caller to render the flat list.
	if (!parts.some(p => isSurveySection(p))) return null;

	const byId = new Map(fields.map(f => [f.id, f] as const));

	/** A field ID is visible if visibleFieldIds is undefined (show all) or contains the ID. */
	const isVisible = (id?: string) => !!id && (visibleFieldIds === undefined || visibleFieldIds.has(id));

	/**
	 * Resolve a fieldId to a renderable IFormField, honouring both
	 * conditional-logic visibility and the admin `isHidden` flag (which
	 * removes a field from the form entirely, independent of logic).
	 */
	const resolve = (fieldId?: string): IFormField<string> | undefined => {
		if (!isVisible(fieldId)) return undefined;
		const f = byId.get(fieldId!);
		return f && !f.isHidden ? f : undefined;
	};

	const groups: SectionGroup[] = [];
	let flat: IFormField<string>[] = [];

	/** Flush accumulated flat questions into an unheadered group. */
	const flushFlat = () => {
		if (flat.length) {
			groups.push({ fields: flat });
			flat = [];
		}
	};

	for (const part of parts) {
		if (isSurveySection(part)) {
			flushFlat();
			const visibleFields = (part.questions ?? []).map(q => resolve(q.fieldId)).filter(Boolean) as IFormField<string>[];
			// Only include the section if it has at least one visible question.
			if (visibleFields.length) {
				groups.push({ section: part, fields: visibleFields });
			}
		} else {
			// Flat question part (V1-style part sitting outside any section).
			const f = resolve((part as { fieldId?: string }).fieldId);
			if (f) flat.push(f);
		}
	}

	flushFlat();
	return groups;
}
